import { useEffect } from 'react';

import { Grid, SelectChangeEvent, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { Controller, useFormContext } from 'react-hook-form';

import LicenseForm from './licenseForm';
import { SelectStyle, MenuItemStyle, FormErrorText } from './style';

import courseAPI, { COURSE_END_POINT } from 'src/api/course';
import { CPDEnrollmentFieldNames } from 'src/helpers/constants';
import { useRequest } from 'src/helpers/use';
import { ApplicantTypeEnum, CPDRegulatorTypeEnum, ILicenseType } from 'src/interfaces/course';

type Props = {
  license?: {
    licenseNumber: string;
    applicantType: ApplicantTypeEnum;
    startDate: string;
    endDate: string;
  };
};

const TSI = ({ license }: Props) => {
  const { control, setValue, getValues } = useFormContext();
  const { t } = useTranslation();
  const { data: licenseTypes } = useRequest<ILicenseType[]>(
    COURSE_END_POINT.LICENSE_TYPE(CPDRegulatorTypeEnum.TSI),
    () => courseAPI.fetchLicenseType(CPDRegulatorTypeEnum.TSI),
  );

  const getLicenseTypeLabel = (value: string) => {
    const licenseType = (licenseTypes || []).find((license: ILicenseType) => license.value === value);
    if (licenseType) return licenseType.name;
    return '';
  };

  const handleOnLicenseTypeChange = (event: SelectChangeEvent<unknown>) => {
    const { value } = event.target;
    setValue(CPDEnrollmentFieldNames.tsiLicenseType, value);
    setValue(CPDEnrollmentFieldNames.tsiLicenseName, getLicenseTypeLabel(value as string));
  };

  useEffect(() => {
    if (license?.applicantType && licenseTypes) {
      const licenseType = (licenseTypes || []).find(
        (item: ILicenseType) => item.applicantType === license.applicantType,
      );
      setValue(
        CPDEnrollmentFieldNames.tsiLicenseType,
        getValues(CPDEnrollmentFieldNames.tsiLicenseType)
          ? getValues(CPDEnrollmentFieldNames.tsiLicenseType)
          : licenseType?.value,
      );
      setValue(CPDEnrollmentFieldNames.tsiLicenseName, licenseType?.name);
    }
  }, [licenseTypes]);

  return (
    <>
      <Grid container alignItems="baseline" spacing={2} marginBottom={3}>
        <Grid xs={12} md={6} item>
          <Controller
            name={CPDEnrollmentFieldNames.tsiLicenseType}
            defaultValue=""
            control={control}
            rules={{
              required: {
                value: true,
                message: t('cpd_enrollment.license_information.error.type'),
              },
            }}
            render={({ field, fieldState: { invalid, error } }) => {
              const { onChange } = field;
              return (
                <>
                  <Typography variant="h7">
                    <Typography variant="h7" component="span" color="errorDark.main">
                      *
                    </Typography>
                    {t('cpd_enrollment.license_information.type')}
                  </Typography>
                  <SelectStyle
                    fullWidth
                    {...field}
                    error={invalid}
                    displayEmpty
                    onChange={(event) => {
                      handleOnLicenseTypeChange(event);
                      onChange(event);
                    }}
                    renderValue={(value: unknown) => (
                      <span>
                        {getLicenseTypeLabel(value as string) ||
                          t('cpd_enrollment.license_information.placeholder.type')}
                      </span>
                    )}
                  >
                    {(licenseTypes || []).map((licenseType) => (
                      <MenuItemStyle key={licenseType.value} value={licenseType.value}>
                        {licenseType.name}
                      </MenuItemStyle>
                    ))}
                  </SelectStyle>
                  {error?.message && <FormErrorText sx={{ mt: '-4px' }}>{error.message}</FormErrorText>}
                </>
              );
            }}
          />
        </Grid>
      </Grid>
      <LicenseForm
        licenseFieldName={CPDEnrollmentFieldNames.tsiLicenseNo}
        startDateFieldName={CPDEnrollmentFieldNames.tsiStartDate}
        endDateFieldName={CPDEnrollmentFieldNames.tsiEndDate}
        license={license?.licenseNumber}
        startDate={license?.startDate}
        endDate={license?.endDate}
        isTSI
      />
    </>
  );
};

export default TSI;
